import React from 'react';
import { clsx } from 'clsx';

export type TagColor =
  | 'gray'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink';

export type TagVariant = 'solid' | 'subtle' | 'outline';
export type TagSize = 'sm' | 'md' | 'lg';

export interface TagData {
  id: string;
  name: string;
  color?: TagColor;
  icon?: string;
  description?: string;
}

export interface TagProps {
  children: React.ReactNode;
  color?: TagColor;
  variant?: TagVariant;
  size?: TagSize;
  icon?: React.ReactNode;
  removable?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

const colorStyles: Record<TagColor, Record<TagVariant, string>> = {
  gray: {
    solid: 'bg-gray-600 text-white',
    subtle: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    outline: 'border-gray-300 text-gray-700 bg-white dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700',
  },
  red: {
    solid: 'bg-red-600 text-white',
    subtle: 'bg-red-100 text-red-800',
    outline: 'border-red-300 text-red-700 bg-white dark:bg-gray-800',
  },
  orange: {
    solid: 'bg-orange-600 text-white',
    subtle: 'bg-orange-100 text-orange-800',
    outline: 'border-orange-300 text-orange-700 bg-white dark:bg-gray-800',
  },
  yellow: {
    solid: 'bg-yellow-500 text-white',
    subtle: 'bg-yellow-100 text-yellow-800',
    outline: 'border-yellow-300 text-yellow-700 bg-white dark:bg-gray-800',
  },
  green: {
    solid: 'bg-green-600 text-white',
    subtle: 'bg-green-100 text-green-800',
    outline: 'border-green-300 text-green-700 bg-white dark:bg-gray-800',
  },
  teal: {
    solid: 'bg-teal-600 text-white',
    subtle: 'bg-teal-100 text-teal-800',
    outline: 'border-teal-300 text-teal-700 bg-white dark:bg-gray-800',
  },
  blue: {
    solid: 'bg-blue-600 text-white',
    subtle: 'bg-blue-100 text-blue-800',
    outline: 'border-blue-300 text-blue-700 bg-white dark:bg-gray-800',
  },
  indigo: {
    solid: 'bg-indigo-600 text-white',
    subtle: 'bg-indigo-100 text-indigo-800',
    outline: 'border-indigo-300 text-indigo-700 bg-white dark:bg-gray-800',
  },
  purple: {
    solid: 'bg-purple-600 text-white',
    subtle: 'bg-purple-100 text-purple-800',
    outline: 'border-purple-300 text-purple-700 bg-white dark:bg-gray-800',
  },
  pink: {
    solid: 'bg-pink-600 text-white',
    subtle: 'bg-pink-100 text-pink-800',
    outline: 'border-pink-300 text-pink-700 bg-white dark:bg-gray-800',
  },
};

const sizeStyles: Record<TagSize, { tag: string; icon: string; remove: string }> = {
  sm: { tag: 'px-1.5 py-0.5 text-xs', icon: 'text-xs', remove: 'ml-1 -mr-0.5' },
  md: { tag: 'px-2 py-1 text-sm', icon: 'text-sm', remove: 'ml-1.5 -mr-0.5' },
  lg: { tag: 'px-3 py-1.5 text-base', icon: 'text-base', remove: 'ml-2 -mr-1' },
};

export function Tag({
  children,
  color = 'gray',
  variant = 'subtle',
  size = 'md',
  icon,
  removable = false,
  onRemove,
  onClick,
  disabled = false,
  className,
}: TagProps) {
  const colorStyle = colorStyles[color][variant];
  const sizeStyle = sizeStyles[size];
  const hasClickHandler = !!onClick;
  const isInteractive = hasClickHandler && !disabled;

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick && !disabled) {
      e.preventDefault();
      onClick();
    }
  };

  const Component = hasClickHandler ? 'button' : 'span';

  return (
    <Component
      type={hasClickHandler ? 'button' : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      disabled={hasClickHandler ? disabled : undefined}
      className={clsx(
        'inline-flex items-center font-medium rounded-full',
        variant === 'outline' && 'border',
        sizeStyle.tag,
        colorStyle,
        isInteractive && 'cursor-pointer hover:opacity-80 transition-opacity',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && <span className={clsx('mr-1', sizeStyle.icon)}>{icon}</span>}
      {children}
      {removable && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className={clsx(
            'rounded-full hover:bg-black hover:bg-opacity-10 transition-colors',
            sizeStyle.remove,
            disabled && 'cursor-not-allowed'
          )}
          aria-label="Remove tag"
        >
          ×
        </button>
      )}
    </Component>
  );
}

// TagGroup - displays multiple tags with consistent spacing
export interface TagGroupProps {
  children: React.ReactNode;
  size?: TagSize;
  wrap?: boolean;
  className?: string;
}

export function TagGroup({
  children,
  size = 'md',
  wrap = true,
  className,
}: TagGroupProps) {
  const gapClass = size === 'sm' ? 'gap-1' : size === 'lg' ? 'gap-2' : 'gap-1.5';

  return (
    <div
      className={clsx(
        'flex items-center',
        gapClass,
        wrap ? 'flex-wrap' : 'overflow-x-auto',
        className
      )}
    >
      {children}
    </div>
  );
}

// ColorDot - shows just a colored dot
export interface ColorDotProps {
  color: TagColor;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const dotSizes = {
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
};

const dotColors: Record<TagColor, string> = {
  gray: 'bg-gray-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  teal: 'bg-teal-500',
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  purple: 'bg-purple-500',
  pink: 'bg-pink-500',
};

export function ColorDot({ color, size = 'md', className }: ColorDotProps) {
  return (
    <span
      className={clsx('inline-block rounded-full', dotSizes[size], dotColors[color], className)}
    />
  );
}

// TagWithDot - tag with colored dot prefix
export interface TagWithDotProps {
  children: React.ReactNode;
  color?: TagColor;
  size?: TagSize;
  removable?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  className?: string;
}

export function TagWithDot({
  children,
  color = 'gray',
  size = 'md',
  removable,
  onRemove,
  onClick,
  className,
}: TagWithDotProps) {
  return (
    <Tag
      color="gray"
      variant="subtle"
      size={size}
      icon={<ColorDot color={color} size={size === 'lg' ? 'md' : 'sm'} />}
      removable={removable}
      onRemove={onRemove}
      onClick={onClick}
      className={className}
    >
      {children}
    </Tag>
  );
}

// Utility to get all available colors
export function getTagColors(): TagColor[] {
  return ['gray', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'indigo', 'purple', 'pink'];
}

// Utility to get color style classes
export function getTagColorClasses(color: TagColor, variant: TagVariant = 'subtle'): string {
  return colorStyles[color][variant];
}

// Utility to generate a consistent color from a string
export function getColorFromString(str: string): TagColor {
  const colors = getTagColors();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}
